import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from '../../routing';
import { commandCenterApi } from '../../utils/api';
import StatInfo from '../../components/StatInfo';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import PeriodPicker from '../../components/PeriodPicker';
import { PRESETS_DOCUMENTS } from '../../utils/period';
import { LoadState, useRefetchOnFocus } from './parts';
import { Dash, Metric, SortTh, fmt, pct } from './statsParts';
import SuppliersView from './SuppliersView';
import ExtractionQualityView from './ExtractionQualityView';
import BackfillReportSection from './BackfillReportSection';

// סטטיסטיקות — platform-wide statistics, cross-tenant, for deciding what to
// work on next. One tab per subject, switched by ?view= like לטיפול; more
// subjects are planned (owner, 27/09/2026), so the tab bar is there from the
// first one rather than retrofitted.
//
// Laid out like /suppliers: the period filter sits in its own bar ABOVE the
// card, and the tabs are the header row of the card that holds the view, not
// a box of their own. The period is page scope, shared by every tab, so it
// lives here rather than in a view and survives switching between them.
//
// מסמכים — the platform's document volume: which document types carry the
// load, and which suppliers send them (lib/ccStats.js). A supplier is ONE LINE
// ACROSS CUSTOMERS — the server folds the per-restaurant supplier rows by
// normalised name — so "לקוחות" on that table is a supplier's reach.
//
// Opens on ALL TIME, unlike לטיפול's 90 days: the question is "how many do we
// have", and there is no row cap to protect — the server runs one GROUP BY.

// `dated`: whether the page's period filter applies to the view. ספקים is the
// current state of the supplier data, not a period, so the filter hides there
// rather than sit on screen implying it narrows something. `note`: the scope
// footnote under the card.
const VIEWS = [
  { key: 'documents', label: 'מסמכים', dated: true,  note: 'כל הלקוחות, ללא מסעדות דמו · לפי תאריך העלאה' },
  { key: 'suppliers', label: 'ספקים',  dated: false, note: 'כל הלקוחות, ללא מסעדות דמו · מצב נוכחי' },
  // איכות קריאה — the stage-0 review measurement (ExtractionQualityView.jsx).
  // Dated like מסמכים, but on the DECISION date, and an empty period means
  // the last 30 days rather than all time — the server's default (spec §7).
  { key: 'extraction', label: 'איכות קריאה', dated: true, note: 'כל הלקוחות, ללא מסעדות דמו · לפי תאריך ההחלטה · טווח ריק = 30 הימים האחרונים' },
];

// The supplier matrix can run to hundreds of lines; the top of it is what a
// prioritisation screen is for, and the rest is one click away.
const SUPPLIER_PAGE = 50;
const NO_SUPPLIER_LABEL = 'לא זוהה ספק';

// How many different document types a supplier sends, across every customer.
const typeCount = (s) => Object.keys(s.by_type).length;

const cellValue = (s, col) => {
  if (col === 'total') return s.total;
  if (col === 'restaurants') return s.restaurants;
  if (col === 'types') return typeCount(s);
  return s.by_type[col] || 0;
};

// ─── מסמכים ─────────────────────────────────────────────────────────────────

function DocumentMetrics({ data }) {
  const noSupplier = data.suppliers.find(s => s.no_supplier);
  return (
    <div className="cc-cardmetrics">
      <Metric label="מסמכים" value={fmt(data.total)} />
      <Metric label="ספקים" value={fmt(data.supplier_count)} detail="ספק אחד בכל הלקוחות" />
      <Metric label="לקוחות" value={fmt(data.restaurants)} detail="עם מסמכים בטווח" />
      <Metric
        label="ללא ספק"
        value={fmt(noSupplier?.total)}
        detail={noSupplier ? `${pct(noSupplier.total, data.total)}% מהמסמכים` : 'כל המסמכים משויכים'}
        tone={noSupplier ? 'alert' : 'good'}
      />
    </div>
  );
}

function ByTypeTable({ rows, total }) {
  return (
    <div className="table-wrapper">
      <table className="cc-bytype">
        <thead>
          <tr>
            <th>סוג מסמך</th>
            <th>מסמכים</th>
            <th style={{ minWidth: 160 }}>חלק מהסך</th>
            <th>טיוטות</th>
            <th>לא תקינים</th>
            <th>לקוחות</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(t => (
            <tr key={t.doc_type}>
              <td style={{ fontWeight: 600 }}>{t.doc_type}</td>
              <td className="cc-num" style={{ fontWeight: 700 }}>{fmt(t.count)}</td>
              <td>
                <div className="cc-share">
                  <span className="cc-share-track">
                    <span className="cc-share-fill" style={{ width: `${pct(t.count, total)}%` }} />
                  </span>
                  <span className="cc-num cc-muted">{pct(t.count, total)}%</span>
                </div>
              </td>
              <td className="cc-num">{t.drafts ? fmt(t.drafts) : <Dash />}</td>
              <td className="cc-num">{t.invalid ? fmt(t.invalid) : <Dash />}</td>
              <td className="cc-num">{fmt(t.restaurants)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function supplierLabel(s) {
  if (s.no_supplier) return <span className="cc-pill bad">{NO_SUPPLIER_LABEL}</span>;
  return s.name.trim() || <span className="cc-muted">ספק ללא שם</span>;
}

// 5–28% tint against the column's own maximum: the lightest still reads as
// "some", the darkest keeps the number legible.
function HeatCell({ n, max }) {
  if (!n) return <td className="cc-num"><Dash /></td>;
  const heat = max ? 5 + Math.round((Math.min(n, max) / max) * 23) : 0;
  return (
    <td className="cc-num" style={{ background: `color-mix(in srgb, var(--peak-green) ${heat}%, transparent)` }}>
      {fmt(n)}
    </td>
  );
}

// The count, and on tap the types behind it with each one's volume — in the
// matrix's own column order (largest type first), so the list reads like the
// row. StatInfo positions itself against the viewport, so the note is not
// clipped by the matrix's scroll box.
function TypesCell({ s, docTypes }) {
  const types = docTypes.filter(t => s.by_type[t]);
  const label = supplierLabel(s);
  return (
    <td className="cc-num">
      <span className="cc-types">
        {fmt(types.length)}
        <StatInfo
          label="סוגי מסמכים"
          ariaLabel={`סוגי המסמכים של ${s.no_supplier ? NO_SUPPLIER_LABEL : s.name}`}
          maxWidth={220}
        >
          <div className="cc-types-title">{label}</div>
          <ul className="stat-info-list">
            {types.map(t => <li key={t}>{t} · {fmt(s.by_type[t])}</li>)}
          </ul>
        </StatInfo>
      </span>
    </td>
  );
}

function MatrixRow({ s, docTypes, colMax }) {
  return (
    <tr>
      <td className="cc-matrix-name">{supplierLabel(s)}</td>
      <td className="cc-num">{fmt(s.restaurants)}</td>
      <TypesCell s={s} docTypes={docTypes} />
      <td className="cc-num" style={{ fontWeight: 700 }}>{fmt(s.total)}</td>
      {docTypes.map(t => <HeatCell key={t} n={s.by_type[t]} max={colMax[t]} />)}
    </tr>
  );
}

function useMatrixRows(suppliers, q, sort) {
  return useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? suppliers.filter(s => (s.no_supplier ? NO_SUPPLIER_LABEL : s.name).toLowerCase().includes(needle))
      : suppliers;
    return [...filtered].sort((a, b) => cellValue(b, sort) - cellValue(a, sort) || b.total - a.total);
  }, [suppliers, q, sort]);
}

// Each column is shaded against ITSELF, so a supplier that dominates קבלות
// stands out even though קבלות are a sliver of the platform's volume. The
// unidentified bucket is left out of the maximum: it is not a supplier, and a
// large pile of it would wash every real supplier down to the palest tint
// (its own cells are capped at the darkest shade by HeatCell).
function useColumnMax(suppliers, docTypes) {
  return useMemo(() => {
    const real = suppliers.filter(s => !s.no_supplier);
    return Object.fromEntries(docTypes.map(t => [t, Math.max(0, ...real.map(s => s.by_type[t] || 0))]));
  }, [suppliers, docTypes]);
}

function SupplierMatrix({ suppliers, docTypes }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('total');
  const [showAll, setShowAll] = useState(false);
  const rows = useMatrixRows(suppliers, q, sort);
  const colMax = useColumnMax(suppliers, docTypes);
  const visible = showAll || q ? rows : rows.slice(0, SUPPLIER_PAGE);

  return (
    <>
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 className="cc-card-title">מסמכים לפי ספק</h2>
        <span className="cc-muted" style={{ fontSize: '0.79rem' }}>
          ספק אחד בכל הלקוחות · לחיצה על כותרת עמודה ממיינת
        </span>
        <span className="cc-spacer" />
        <input
          className="form-control"
          style={{ maxWidth: 220 }}
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="חיפוש ספק…"
          aria-label="חיפוש ספק"
        />
      </div>

      {rows.length === 0 ? (
        <div className="cc-empty">אין ספק שתואם לחיפוש</div>
      ) : (
        <div className="cc-matrix-scroll">
          <table className="cc-matrix">
            <thead>
              <tr>
                <th className="cc-matrix-name">ספק</th>
                <SortTh col="restaurants" sort={sort} onSort={setSort}>לקוחות</SortTh>
                <SortTh col="types" sort={sort} onSort={setSort}>סוגי מסמכים</SortTh>
                <SortTh col="total" sort={sort} onSort={setSort}>סה״כ</SortTh>
                {docTypes.map(t => <SortTh key={t} col={t} sort={sort} onSort={setSort}>{t}</SortTh>)}
              </tr>
            </thead>
            <tbody>
              {visible.map(s => <MatrixRow key={s.key} s={s} docTypes={docTypes} colMax={colMax} />)}
            </tbody>
          </table>
        </div>
      )}

      {!q && !showAll && rows.length > SUPPLIER_PAGE && (
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={() => setShowAll(true)}>
          הצג את כל {fmt(rows.length)} הספקים
        </button>
      )}
    </>
  );
}

function useDocumentStats(dateFrom, dateTo) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const loadToken = useRef(0);

  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setError(false);
    try {
      const r = await commandCenterApi.getDocumentStats({ from: dateFrom, to: dateTo });
      if (token !== loadToken.current) return;   // superseded by a newer range
      setData(r.data);
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setData(null);
    }
  }, [dateFrom, dateTo]);

  // A new range clears the screen; a focus refetch (useRefetchOnFocus) does
  // not, so returning to the tab never flashes "טוען…" over good numbers.
  useEffect(() => { setData(null); load(); }, [load]);
  useRefetchOnFocus(load);
  return { data, error, load };
}

function DocumentsView({ dateFrom, dateTo }) {
  const { data, error, load } = useDocumentStats(dateFrom, dateTo);

  return (
    <LoadState
      loading={!data && !error}
      error={error}
      onRetry={load}
      empty={data?.total === 0}
      emptyMessage="אין מסמכים בטווח שנבחר"
    >
      {data && (
        <>
          <DocumentMetrics data={data} />
          <section className="cc-tabpanel-section">
            <div className="cc-card-head">
              <h2 className="cc-card-title">מסמכים לפי סוג</h2>
            </div>
            <ByTypeTable rows={data.by_type} total={data.total} />
          </section>
          <section className="cc-tabpanel-section">
            <SupplierMatrix suppliers={data.suppliers} docTypes={data.doc_types} />
          </section>
        </>
      )}
    </LoadState>
  );
}

// ─── The ספקים tab's ⋮ ───────────────────────────────────────────────────────

// Rare, one-off supplier actions live behind ⋮ rather than on the tab itself
// (owner, 28/09/2026): החלת דוח ח.פ. is run once per backfill, not read daily.
// It opens in a modal; an apply remounts the tab so its figures re-read.
function SuppliersMenu({ onApplied }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  return (
    <div className="page-menu">
      <button type="button" className="page-menu-trigger" aria-label="פעולות נוספות"
        aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)}>⋮</button>
      {menuOpen && (
        <>
          <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="page-menu-panel">
            <button type="button" className="page-menu-item"
              onClick={() => { setMenuOpen(false); setBackfillOpen(true); }}>
              <Icon name="upload" size={15} /> החלת דוח ח.פ.
            </button>
          </div>
        </>
      )}
      {backfillOpen && (
        <Modal onClose={() => setBackfillOpen(false)} size={760} ariaLabel="החלת דוח ח.פ.">
          <BackfillReportSection onApplied={onApplied} />
        </Modal>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function StatsPage() {
  const [params, setParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const setDateRange = (from, to) => { setDateFrom(from || ''); setDateTo(to || ''); };
  const current = VIEWS.find(v => v.key === params.get('view')) || VIEWS[0];
  const view = current.key;
  // Bumped by an apply from the ⋮ modal: remounting the tab re-reads it.
  const [suppliersRev, setSuppliersRev] = useState(0);

  const setView = (key) => {
    const next = new URLSearchParams(params);
    if (key === VIEWS[0].key) next.delete('view'); else next.set('view', key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <div className="cc-title-row">
        <h1 className="cc-page-title">סטטיסטיקות</h1>
        {view === 'suppliers' && <SuppliersMenu onApplied={() => setSuppliersRev(n => n + 1)} />}
      </div>

      {current.dated && (
        <div className="cc-filter-wrap">
          <div className="docs-filter-bar">
            <PeriodPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} presets={PRESETS_DOCUMENTS} variant="pill" />
          </div>
        </div>
      )}

      <div className="cc-card cc-tabpanel">
        <div className="view-tabs" role="tablist" aria-label="סוג סטטיסטיקה">
          {VIEWS.map(v => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={`view-tab${view === v.key ? ' is-active' : ''}`}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        {view === 'documents' && <DocumentsView dateFrom={dateFrom} dateTo={dateTo} />}
        {view === 'suppliers' && <SuppliersView key={suppliersRev} />}
        {view === 'extraction' && <ExtractionQualityView dateFrom={dateFrom} dateTo={dateTo} />}
      </div>

      {/* The scope of every figure above, stated once as a footnote rather
          than competing with the filter for the top of the page. */}
      <p className="cc-page-note">{current.note}</p>
    </>
  );
}
